import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/toast";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { Button } from "../components/button";
import { ErrorMessage } from "../components/error-message";
import { EditableField } from "../components/editable-field";

type SignInFormProps = {
  route?: {
    pathname?: string;
    search?: string;
  };
};

export const SignInForm = ({ route = {} }: SignInFormProps) => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { handleError, error, clearError } = useErrorHandler();
  const handleLogin = async (e: React.FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();

    clearError();

    try {
      const { username, password } = formData;
      const trimmedUsername = username.trim();
      const trimmedPassword = password.trim();

      if (!trimmedUsername || !trimmedPassword) {
        handleError("Por favor, completa todos los campos.");
        return;
      }

      const response = await login(trimmedUsername, trimmedPassword);

      if (response.statusCode === 200) {
        addToast({
          message: "¡Sesión iniciada!",
          type: "success",
          location: "bottom-center",
          duration: 1500,
        });
        const redirectPath = route.pathname || "/home";
        const search = route.search || "";
        navigate(redirectPath + search);
      } else {
        handleError(response.message || "Error al iniciar sesión", {
          persist: true,
        });
      }
    } catch (error) {
      handleError("Error inesperado. Intente nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLElement>) => {
    if (error) clearError();
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      <form onSubmit={handleLogin} className="space-y-4">
        <EditableField
          type="text"
          label="Nombre de Usuario"
          name="username"
          id="username"
          required
          requiredAsterisk={false}
          value={formData.username}
          onChange={handleChange}
          minLength={6}
          maxLength={35}
          title="Ingresa un nombre de usuario válido (ejemplo: usuario123)."
          placeholder="Ingresa tu nombre de usuario"
        />

        <EditableField
          label="Contraseña"
          type="password"
          name="password"
          id="password"
          required
          requiredAsterisk={false}
          value={formData.password}
          onChange={handleChange}
          minLength={6}
          maxLength={35}
          title="La contraseña debe tener al menos 6 caracteres y no más de 35."
          placeholder="Ingresa tu contraseña"
        />

        {error && <ErrorMessage message={error} dismissible />}
        <Button
          type="submit"
          disabled={isSubmitting || error !== null}
          text="Iniciar Sesión"
          variant="auth"
        />
      </form>
    </>
  );
};
